import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* ------------------------------------------------------------------ *
 * Responsive Bildquellen.
 *
 * Die Varianten liegen als `<name>-480.<ext>` / `<name>-960.<ext>` /
 * `<name>-1600.<ext>` neben dem Original (erzeugt von scripts/build-images.py).
 * Die 1600er ist für die großen Case-Flächen (Startseite, Detail-Hero) auf
 * Retina-Displays — dort wäre die 960er sichtbar weich.
 *
 * Die Pfade wurden vorher direkt im JSX über
 * `image.replace('.jpg', '-480.avif')` gebaut. Das funktioniert genau so
 * lange, wie jedes Case-Bild eine .jpg ist: Der erste Eintrag mit .png oder
 * .webp hätte still auf die unveränderte Originaldatei gezeigt — kein
 * Fehler, kein leeres Bild, nur wieder die große Datei. Hier wird die
 * Endung erkannt und im Zweifel `null` zurückgegeben, damit der Aufrufer
 * sichtbar auf ein einfaches <img> zurückfällt.
 * ------------------------------------------------------------------ */

export interface ResponsiveImage {
  /** srcSet-Wert je Format, 480w + 960w + 1600w. */
  avif: string;
  webp: string;
  jpeg: string;
  /** Quelle für das <img> selbst: die 960er JPEG, nicht das Original. */
  fallback: string;
}

const IMAGE_EXT = /\.(jpe?g|png|webp|avif)$/i;

export function responsiveImage(src: string): ResponsiveImage | null {
  const match = src.match(IMAGE_EXT);
  if (!match) return null;

  const base = src.slice(0, src.length - match[0].length);
  const set = (ext: string) =>
    `${base}-480.${ext} 480w, ${base}-960.${ext} 960w, ${base}-1600.${ext} 1600w`;

  return {
    avif: set('avif'),
    webp: set('webp'),
    jpeg: set('jpg'),
    fallback: `${base}-960.jpg`,
  };
}

/* ------------------------------------------------------------------ *
 * Kontrast-Rechnung für die Akzentfarben der Cases/Services/Blog.
 *
 * Die Badges standen als Text in Markenfarbe auf einer Fläche aus
 * derselben Markenfarbe mit 12,5 % Deckkraft (`${color}20`). Farbton
 * gleich, nur Helligkeit verschieden — auf den dunklen Karten trägt das
 * kaum Kontrast, und Prüfwerkzeuge, die die Alpha-Fläche als Volltonfarbe
 * lesen, messen 1.0 : 1.
 *
 * Statt einer gepflegten Hex-Map wird die Textfarbe jetzt gerechnet: die
 * Markenfarbe wird in HSL so weit aufgehellt, bis sie gegen den
 * tatsächlichen Kartenhintergrund ≥ 4.6 : 1 erreicht (WCAG AA für Text
 * < 18,66 px, mit etwas Luft). Farbton und Sättigung bleiben unangetastet,
 * die Marke bleibt erkennbar. Neue Case-Farben sind damit automatisch
 * abgedeckt.
 *
 * Bezugsfläche ist #070707: die Karten sind `bg-card/50`
 * (hsl(0 0% 5%) = #0d0d0d bei 50 %) über dem schwarzen Sektionsgrund,
 * also 0x0d × 0.5 = 6.5 → aufgerundet 7. Aufrunden ist die strengere
 * Richtung: ein hellerer Bezugsgrund senkt das gemessene Verhältnis, die
 * Funktion hebt die Farbe dadurch eher zu weit an als zu wenig.
 * ------------------------------------------------------------------ */

/** Kartenhintergrund, gegen den gemessen wird (bg-card/50 auf #000). */
const CARD_BG: [number, number, number] = [7, 7, 7];

/**
 * WCAG AA für Text unter 18,66 px, plus Puffer.
 *
 * 4.8 statt 4.6: der Bezugsgrund hier ist ein gerundeter Näherungswert, im
 * gerenderten DOM kommen Blur- und Gradient-Layer dazu. Mit 4.6 landete die
 * Messung am realen Aufbau bei 4.51 — über der Grenze, aber ohne Reserve für
 * die nächste Akzentfarbe.
 */
const TEXT_TARGET = 4.8;

/** WCAG 1.4.11 für Nicht-Text (Rahmen, Icons). */
const UI_TARGET = 3.0;

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function toHex([r, g, b]: [number, number, number]): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const ch = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

function contrast(a: [number, number, number], b: [number, number, number]): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [channel(h + 1 / 3) * 255, channel(h) * 255, channel(h - 1 / 3) * 255];
}

const textColorCache = new Map<string, string>();

/**
 * Hebt eine Akzentfarbe so weit an, bis sie als Text auf der dunklen
 * Karte WCAG AA (≥ 4.5 : 1) erfüllt. Farben, die das schon tun, kommen
 * unverändert zurück.
 */
export function getAccessibleTextColor(color: string): string {
  const cached = textColorCache.get(color);
  if (cached) return cached;

  const rgb = parseHex(color);
  let result = color;
  if (contrast(rgb, CARD_BG) < TEXT_TARGET) {
    const [h, s, l] = rgbToHsl(rgb);
    for (let i = 1; i <= 200; i++) {
      const candidate = hslToRgb([h, s, Math.min(1, l + ((1 - l) * i) / 200)]);
      if (contrast(candidate, CARD_BG) >= TEXT_TARGET) {
        result = toHex(candidate);
        break;
      }
    }
  }
  textColorCache.set(color, result);
  return result;
}

const borderColorCache = new Map<string, string>();

/**
 * Rahmenfarbe für Badges: dieselbe (angehobene) Akzentfarbe, so weit
 * heruntergedeckt wie möglich, aber nie unter 3 : 1 gegen die Karte —
 * die Grenze für Nicht-Text-Kontrast (WCAG 1.4.11). Ersetzt die getönte
 * Füllfläche, damit Text und Fläche nicht mehr denselben Farbton teilen.
 */
export function getBadgeBorderColor(color: string): string {
  const cached = borderColorCache.get(color);
  if (cached) return cached;

  const [r, g, b] = parseHex(getAccessibleTextColor(color));
  let result = toHex([r, g, b]);
  for (let a = 30; a <= 100; a += 1) {
    const alpha = a / 100;
    const mixed: [number, number, number] = [
      r * alpha + CARD_BG[0] * (1 - alpha),
      g * alpha + CARD_BG[1] * (1 - alpha),
      b * alpha + CARD_BG[2] * (1 - alpha),
    ];
    if (contrast(mixed, CARD_BG) >= UI_TARGET) {
      result = `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
      break;
    }
  }
  borderColorCache.set(color, result);
  return result;
}

/**
 * Schriftfarbe auf einer vollflächigen Markenfarbe (Buttons, Kreise).
 * Vorher stand dort fest `text-black` — bei dunklen Markenfarben wie dem
 * KRAFT-Blau (#12335F) oder dem vCard-Braun (#6E5235) war die Schrift damit
 * kaum lesbar. Gewählt wird Schwarz oder Weiß, je nachdem was mehr Kontrast hat.
 */
export function getOnColor(color: string): '#000000' | '#ffffff' {
  const rgb = parseHex(color);
  return contrast(rgb, [0, 0, 0]) >= contrast(rgb, [255, 255, 255]) ? '#000000' : '#ffffff';
}
